import { UISprite } from "../UI/Tools/Base/UISprite";
import { Core } from "../Base/Core";
import { Random } from "../Utils/Unity/Random";
import { gsap } from "gsap";
import { UIButton } from "../UI/Tools/Base/UIButton";
import { UILabeledIcon } from "../UI/Tools/Base/UILabeledIcon";
import { Utils } from "../Utils/Utils";
import { SlotItem } from "../enums";
import { SlotMan } from "./SlotMan";
import { NUtils } from "../Utils/NUtils";
import { GameManager } from "../Game/GameManager";

const ANIMATION_IDLE = "idle";
const ANIMATION_SPIN = "spin";
const ANIMATION_COMBO = "combo";

class SpinButton extends UIButton {
    constructor() {
        super("slots/slotButton");
    }

    _pressed = false;

    get pressed() {
        return !!this._pressed;
    }

    set pressed(value) {
        this._pressed = !!value;
        this.setImage(this._pressed ? "slots/slotButtonPressed" : "slots/slotButton");
        if (!this._pressed) this._removeTapHighlight();
    }

    Awake() {
        this.on("pointerdown", () => {
            this.pressed = true;
        });
        this.on("pointerup", () => {
            this.pressed = false;
        });
        this.on("pointerout", () => {
            this.pressed = false;
        });
        this.on("pointerupoutside", () => {
            this.pressed = false;
        });
        this.on("pointercancel", () => {
            this.pressed = false;
        });
    }
}

class SlotScreen extends UISprite {
    Awake() {
        super.Awake();
        this.back = this.addChild(new UISprite("slots/slotscreen"));
        this.label = this.addChild(
            new UILabeledIcon({
                label: "",
                icon: "",
                iconAlign: "right",
                iconSize: 0.5,
                textProps: {
                    fill: 0x000000,
                    dropShadow: false,
                },
            })
        );
    }

    // SlotItemReward
    showReward(reward = null, hideTimeout = 0) {
        let type = reward ? reward.itemType : null;
        let icon = "",
            text = "";
        switch (type) {
            case SlotItem.Creature1:
            case SlotItem.Creature2:
                text = Core.Localizator.Get("creature_reward");
                break;

            case SlotItem.MoneyHard:
                icon = "ico-diamond";
                text = reward.reward.value;
                break;

            case SlotItem.MoneySoft1:
            case SlotItem.MoneySoft2:
                icon = "ico-gold";
                text = NUtils.FormatMoney(reward.reward.value * GameManager.gameController.MoneySoftSpeed);
                break;

            case SlotItem.Spin:
                icon = "spin-icon";
                text = reward.reward.valueInt;
                break;
        }

        this.label.icon = icon;
        this.label.text = text;
        this.label.SetActive(icon || text);

        this.fitLayout();

        if (this.label.Active && hideTimeout) {
            Core.WaitBefore(hideTimeout, () => this.showReward(null));
        }
    }

    fitLayout() {
        let { width, height } = this.back.texture;
        let rect = new PIXI.Rectangle(-width * 0.5, -height * 0.5, width, height);
        this.back.fitRect(rect);
        this.label.fitLayout(rect.clone().pad(-width * 0.1, -height * 0.1));
    }
}

class SpinsScale extends UISprite {
    Awake() {
        super.Awake();
        this.back = this.addChild(new UISprite("slots/slotscaleRed"));
        this.fill = this.addChild(new UISprite("slots/slotscaleGreen"));
        this.fill.anchor.set(0, 0);
        this.label = this.addChild(
            new UILabeledIcon({
                label: "0/0",
                textProps: {
                    fitTextTemplate: "00/00",
                    // fontSize: 200
                },
            })
        );
        this.fill.mask = this.fill
            .addChild(new PIXI.Graphics())
            .beginFill(0xffffff, 1)
            .drawRect(0, 0, this.fill.texture.width, this.fill.texture.height);
    }

    fitLayout() {
        this.Update();
        let { width, height } = this.back.texture;
        let rect = new PIXI.Rectangle(-width * 0.5, -height * 0.5, width, height);
        this.back.fitRect(rect);
        this.fill.fitRect(rect);
        this.label.fitLayout(rect);
    }

    Update() {
        let spins = Core.Player.SpinsNumber;
        let maxSpins = Core.Player.MaxSpinsNumber;
        let spinsPercentage = Math.max(0, Math.min(1, spins / maxSpins));

        this.label.text = Utils.Format("{0}/{1}", spins, maxSpins);
        this.fill.mask.scale.x = spinsPercentage;
    }
}

class SlotLaneItem extends UISprite {
    Awake() {
        super.Awake();
        this.picture = this.addChild(new UISprite());
        this.blur = this.addChild(new UISprite());
        this.blur.alpha = 0.5;
        this.itemType = undefined;
        this._rect = new PIXI.Rectangle(0, 0, 1, 1);
        this.setSlotItem(null); // Initial random image
    }

    setImage(img) {
        this.picture.setImage(img);
        this.blur.setImage(img);
    }

    setSlotItem(type) {
        if (this.itemType === type) return; // TODO: Possible bug on creature types! Creature image may change on the same type.
        this.itemType = type;

        let item = Core.GameData.Slots.GetComboRewardForItem(type);
        switch (type) {
            case SlotItem.Creature1:
                this.setImage(`creatures/${Core.Player.PlanetsSave.CurrentPlanet.GetCollectionCreatureWithOffset(item.reward.valueInt)}`);
                break;
            case SlotItem.Creature2:
                this.setImage(`creatures/${Core.Player.PlanetsSave.CurrentPlanet.GetCollectionCreatureWithOffset(item.reward.valueInt)}`);
                break;
            case SlotItem.MoneyHard:
                this.setImage("slots/item-gems");
                break;
            case SlotItem.MoneySoft1:
                this.setImage("slots/item-coin");
                break;
            case SlotItem.MoneySoft2:
                this.setImage("slots/item-bag");
                break;
            case SlotItem.Spin:
                this.setImage("slots/item-spin");
                break;
            default:
                return this.setSlotItem(
                    ~~[...Core.GameData.Slots.helpComboRewards.keys()][~~Random.Range(0, Core.GameData.Slots.helpComboRewards.size - 1)]
                );
        }
        this.fitLayout();
    }

    setSpeedBlur(k) {
        k = Math.max(-1, Math.min(1, k));
        this.blur.pivot.y = this._rect.height * 0.2 * (k * k);
    }

    fitLayout(rect = this._rect) {
        this._rect = rect.clone();
        this.position.copyFrom(this._rect);

        rect = this._rect.clone();

        rect.x = rect.y = 0;
        this.picture.fitRect(rect);

        this.blur.fitRect(rect);
    }
}

class SlotLane extends UISprite {
    constructor(idx) {
        super();
        this.index = idx;
        this.spinning = null;
        this.teasering = false;
    }

    get traveledDistance() {
        // 0 = center of first item in sequence
        return this._traveledDistance;
    }

    set traveledDistance(val) {
        let delta = val - this._traveledDistance;
        this._traveledDistance = val;
        this._renderTraveledDistance();
        if (this.spinning) {
            this.setSpeedBlur(delta * 10);
        }
    }

    Awake() {
        super.Awake();

        this._rect = new PIXI.Rectangle(0, 0, 1, 1);

        this._traveledDistance = 0;
        this.sequence = [];
        this.items = [];
        for (let i = 0; i < 9; i++) {
            let item = this.addChild(new SlotLaneItem());
            this.items.push(item);
        }

        this.reset();
    }

    reset() {
        this.teasering = false;
        if (this.spinning) {
            this.spinning.kill();
            this.spinning = null;
        }
        this.sequence.splice(0);
        this._generateSequence();
    }

    _renderTraveledDistance() {
        // 0 = center of first item in sequence
        let currentItemIndex = Math.round(this._traveledDistance);
        let midItem = Math.floor(this.items.length / 2);
        let startIndex = currentItemIndex - midItem;
        if (startIndex < 0) {
            startIndex += this.sequence.length;
        }

        let sequence = this.sequence.slice(startIndex, startIndex + this.items.length);
        if (sequence.length < this.items.length) {
            sequence.push(...this.sequence.slice(0, this.items.length - sequence.length));
        }

        let itemSize = this._rect.width,
            padding = itemSize * 0.1;
        let cellRect = new PIXI.Rectangle(0, -itemSize * (this.items.length * 0.5) + itemSize, itemSize, itemSize);
        let offset = Math.round(this._traveledDistance) - this._traveledDistance;
        let lensRatio = (1 - this.index) * 0.05;

        let h = cellRect.height * this.items.length;
        let top = cellRect.y;
        let mid = top + h * 0.5 - itemSize * 0.5;

        for (let i = 0; i < this.items.length; i++) {
            let itemRect = cellRect.clone().pad(-padding);
            this.items[i].setSlotItem(sequence[sequence.length - i - 1]);
            this.items[i].fitLayout(itemRect);
            this.items[i].pivot.y = offset * itemSize;

            let itemOffset = Math.abs(mid - cellRect.y + this.items[i].pivot.y) / itemSize;
            this.items[i].pivot.x = itemOffset * itemOffset * itemSize * -lensRatio;

            let k = (midItem - itemOffset) / midItem;
            this.items[i].alpha = k;
            k = 0.5 + 0.5 * k;
            this.items[i].picture.scale.x *= k;
            this.items[i].picture.scale.y *= k;
            this.items[i].blur.scale.x *= k;
            this.items[i].blur.scale.y *= k;

            cellRect.y += itemSize;
        }
    }

    _generateSequence(type = null, teaseringType = null) {
        let length = 30;
        this.sequence = this.sequence || [];
        while (this.sequence.length < length) {
            let t = ~~[...Core.GameData.Slots.helpComboRewards.keys()][~~Random.Range(0, Core.GameData.Slots.helpComboRewards.size - 1)];
            this.sequence.push(t);
        }
        this._truncateTraveledDistance();
        if (type !== null) {
            this.sequence[length - 4] = type;
        }
        if (teaseringType !== null) {
            this.sequence[length - 5] = teaseringType;
        }
        this._renderTraveledDistance();
    }

    _truncateTraveledDistance() {
        this._traveledDistance = Math.round(this._traveledDistance);
        if (this._traveledDistance === 0) return; // already on zero
        while (this._traveledDistance < 0) this._traveledDistance += this.sequence.length;
        while (this._traveledDistance > this.sequence.length) this._traveledDistance -= this.sequence.length;
        while (this._traveledDistance > 0) {
            this.sequence.push(this.sequence.shift());
            this._traveledDistance--;
        }
    }

    setSpeedBlur(k) {
        for (let item of this.items) {
            item.setSpeedBlur(k);
        }
    }

    Spin(type, teaseringType = null) {
        this._generateSequence(type, teaseringType);
        let duration = 2 + 1 * this.index;
        let teasering = teaseringType === null ? Math.random() * 0.1 : 0.3 + Math.random() * 0.4;
        let target = this.sequence.length - 4;
        let prevDistance = this.traveledDistance;
        this.spinning = gsap
            .timeline({ delay: 0.3 * this.index })
            .to(this, {
                traveledDistance: target - teasering,
                duration,
                ease: "circ.out",
            })
            .to(this, {
                traveledDistance: target,
                duration: 0.5,
                ease: "back.out",
            })
            .eventCallback("onUpdate", () => {
                let d = Math.ceil(this.traveledDistance);
                if (prevDistance !== d) {
                    prevDistance = d;
                    Core.Audio.Play("SpinTic");
                }
            })
            .eventCallback("onComplete", () => {
                this.spinning = null;
                this.setSpeedBlur(0);
                Core.Audio.Play("SpinStop");
                this.emit("Finished");
            });
    }

    fitLayout(rect = this._rect) {
        this._rect.copyFrom(rect);
        this.position.copyFrom(rect);
        rect = rect.clone();
        rect.x = rect.y = 0;
        // this._g = (this._g || this.addChild(new PIXI.Graphics)).clear().lineStyle(5, 0xFF0000, 1).drawRect(rect.x, rect.y, rect.width, rect.height);
        this._renderTraveledDistance();
    }
}

class SlotLanes extends UISprite {
    Awake() {
        super.Awake();

        this.lanes = [];
        for (let i = 0; i < 3; i++) {
            this.lanes.push(this.addChild(new SlotLane(i)));
        }

        this.lanes[this.lanes.length - 1].on("Finished", () => {
            this.emit("Finished");
        });

        this.mask = this.addChild(new UISprite("slots/mask"));
    }

    fitLayout(rect) {
        this.position.copyFrom(rect);
        rect = rect.clone();
        rect.x = rect.y = 0;

        if (this.mask) {
            let maskRect = rect.clone();
            this.mask.fitRect(maskRect);
        }

        rect.width *= 1 / this.lanes.length;
        rect.y += 100; // Arrow offset on machine body asset
        for (let lane of this.lanes) {
            lane.fitLayout(rect);
            rect.x += rect.width;
        }
    }

    reset() {
        for (let lane of this.lanes) lane.reset();
    }

    Spin(items, teaseringItem = null) {
        for (let i = 0; i < items.length; i++) {
            this.lanes[i].Spin(items[i], i === this.lanes.length - 1 ? teaseringItem : null);
        }
        Core.Audio.Play("SpinStart");
    }
}

export class SlotMachine extends UISprite {
    Awake() {
        super.Awake();

        this.slotman = this.addChild(new SlotMan());
        this.body = this.addChild(new UISprite("slots/slotmain"));
        this.lampLeft = this.body.addChild(new UISprite("slots/slotLampLeft"));
        this.lampRight = this.body.addChild(new UISprite("slots/slotLampRight"));
        this.eyeLeft = this.body.addChild(new UISprite("slots/slotEyeLeft"));
        this.eyeRight = this.body.addChild(new UISprite("slots/slotEyeRight"));
        this.slotScreen = this.body.addChild(new SlotScreen());
        this.spinsScale = this.body.addChild(new SpinsScale());
        this.timerField = this.body.addChild(
            new UILabeledIcon({
                label: "0/0",
                textProps: {
                    fill: 0x000000,
                    dropShadow: false,
                },
            })
        );

        this.lanes = this.body.addChild(new SlotLanes());
        this.lanes.on("Finished", () => this.emit("Finished"));

        this.spinBtn = this.body.addChild(new SpinButton());
        this.spinBtn.anchor.set(0.5, 1);

        this.restoreSpinsPrice = this.addChild(
            new UILabeledIcon({
                label: "1",
                icon: "ico-diamond",
                iconAlign: "right",
                iconSize: 0.8,
            })
        );

        this._animations = {};
        this._createAnimations();
    }

    _createAnimations() {
        let allLamps = [this.lampLeft, this.lampRight, this.eyeLeft, this.eyeRight, this.slotScreen.back];

        this._animations[ANIMATION_IDLE] = gsap
            .timeline({ repeat: -1, repeatDelay: 1 })
            .set(allLamps, { alpha: 0 }, 0)
            .to([this.lampLeft, this.lampRight], { alpha: 1, duration: 0.1 })
            .to([this.lampLeft, this.lampRight], { alpha: 0, duration: 0.3 })
            .pause(0);

        this._animations[ANIMATION_SPIN] = gsap
            .timeline({ repeat: -1, repeatDelay: 0 })
            .set(allLamps, { alpha: 0 }, 0)
            .to(this.lampRight, { alpha: 1, duration: 0.1 })
            .to(this.lampRight, { alpha: 0, duration: 0.1 })
            .to(this.lampLeft, { alpha: 1, duration: 0.1 })
            .to(this.lampLeft, { alpha: 0, duration: 0.1 })
            .to(this.eyeLeft, { alpha: 1, duration: 0.1 })
            .to(this.eyeLeft, { alpha: 0, duration: 0.1 })
            .to(this.eyeRight, { alpha: 1, duration: 0.1 })
            .to(this.eyeRight, { alpha: 0, duration: 0.1 })
            .pause(0);

        this._animations[ANIMATION_COMBO] = gsap
            .timeline({ repeat: 2, repeatDelay: 0 })
            .set(allLamps, { alpha: 0 }, 0)
            .to(allLamps, { alpha: 1, duration: 0.1 })
            .to(allLamps, { alpha: 0, duration: 0.2 })
            .eventCallback("onComplete", () => this.runAnimation(ANIMATION_IDLE))
            .pause(0);
    }

    runAnimation(name) {
        this.stopAnimations();
        if (this._animations[name]) {
            this._animations[name].play();
        } else {
            console.log("UNKNOWN SLOT ANIMATION", name);
        }
    }

    stopAnimations() {
        for (let anim in this._animations) {
            this._animations[anim].pause(0);
        }
    }

    fitLayout(rect) {
        rect = rect.clone().pad(-rect.width * 0.15, -rect.height * 0.15);
        rect.y += rect.height * 0.05;

        this.position.set(rect.x + rect.width * 0.5, rect.y + rect.height * 0.5);
        this.body.fitRect(rect);

        let lanesRect = this.body.getLocalBounds();
        lanesRect.pad(-lanesRect.width * 0.251, -lanesRect.height * 0.311);
        lanesRect.x -= lanesRect.width * 0.014;
        lanesRect.y -= lanesRect.height * 0.145;
        this.lanes.fitLayout(lanesRect);

        this.slotman.scale.copyFrom(this.body.scale);
        this.slotman.position.set(-50, -860);
        this.slotman.fitLayout();

        this.eyeLeft.scale.set(4.5);
        this.eyeLeft.position.set(-190, -760);

        this.eyeRight.scale.set(4.5);
        this.eyeRight.position.set(150, -760);

        this.lampLeft.scale.set(4.5);
        this.lampLeft.position.set(-395, 380);

        this.lampRight.scale.set(4.5);
        this.lampRight.position.set(395, 380);

        this.slotScreen.fitLayout();
        this.slotScreen.position.set(-20, -550);

        this.spinsScale.fitLayout();
        this.spinsScale.position.set(-8, 328);

        let timerRect = new PIXI.Rectangle(-200, 400, 400, 60);
        this.timerField.fitLayout(timerRect);

        this.spinBtn.position.set(0, 900);

        let restoreRect = new PIXI.Rectangle(-200, 750, 400, 100);
        this.restoreSpinsPrice.fitLayout(restoreRect);
    }

    Show() {
        this.lanes.reset();
        this.slotman.animate();
        this.restoreSpinsPrice.text = Core.GameData.Slots.purchaseSpinsPrice.priceValue.toString();
        this.runAnimation(ANIMATION_IDLE);
        this.slotScreen.showReward(null);
        this.Update();
        super.Show();
    }

    Hide() {
        this.slotman.stop();
        this.stopAnimations();
        super.Hide();
    }

    Update() {
        this.spinsScale.Update();
        this.restoreSpinsPrice.SetActive(Core.Player.SpinsNumber == 0);
        this.updateTimer();
    }

    updateTimer() {
        this.timerField.SetActive(Core.Time.Initialized && Core.Player.SpinsNumber < Core.Player.MaxSpinsNumber);
        if (!this.timerField.Active) return;

        let secondsFromLastRestore = Core.Player.Timers.TicksFromLastSpinsRestore / 1000;
        let secondsLeft = Core.GameData.Config.spinsRestorePeriod - secondsFromLastRestore;
        this.timerField.SetActive(true);
        this.timerField.text = Core.Localizator.Get(
            "slot_spins",
            Core.GameData.Slots.restoreSpinsNumberByTime,
            NUtils.FormatSecondsToTime(secondsLeft)
        );
    }

    showResult(reward, isCombo = false, hideTimeout = 0) {
        this.slotScreen.showReward(reward, hideTimeout);
        if (reward && isCombo) {
            this.runAnimation(ANIMATION_COMBO);
        } else {
            this.runAnimation(ANIMATION_IDLE);
        }
    }

    Spin(items, teasering = false) {
        if (typeof items === "number") {
            items = [items, items, items];
        }
        let teaseringItem = teasering ? items[0] : null;
        this.runAnimation(ANIMATION_SPIN);
        this.lanes.Spin(items, teaseringItem);
        this.emit("Started");
    }
}
